import React, { useEffect, useRef, useState } from "react";
import { Alert, Box, Button, IconButton, Slide, Snackbar, Typography } from "@mui/material";
import { Close as CloseIcon, NotificationsActiveOutlined } from "@mui/icons-material";
import { useLocation } from "react-router-dom";
import Axios from "../../../config/axios";
import { UrlConstant } from "../../../commom/UrlConstant";
import { getUserFromLocalStorage } from "../../../utils/utils";
import { trackEvent } from "../../../config/firebase";
import {
  hasBrowserPushApis,
  isPushSupported,
  isWebPushEnabled,
  registerDevice,
  requestPermissionFromUserAction,
} from "../../../services/pushNotifications";
import { canShowPushPrompt, clearPushPromptState, recordPushPromptDismissed } from "../../../services/pushPromptPolicy";

export const SHOW_DELAY_MS = 12_000;
/** Asked at most once per browser session, and only on the calm home screen (never mid-transfer or mid-trade). */
const SESSION_FLAG = "wolfpack_push_prompt_session";
const CALM_ROUTES = ["/dashboard"];

const track = (name: string) => {
  try {
    trackEvent(name);
  } catch {
    /* analytics must never break the UI */
  }
};

const sessionFlag = (set?: boolean): boolean => {
  try {
    if (set) sessionStorage.setItem(SESSION_FLAG, "1");
    return sessionStorage.getItem(SESSION_FLAG) === "1";
  } catch {
    return false;
  }
};

/** True when every condition for a polite, useful ask is met. */
async function isEligible(userId: string | undefined): Promise<boolean> {
  const user = getUserFromLocalStorage();
  if (!userId || !user?.isVerify || sessionStorage.getItem("pinVerified") !== "true") return false; // signed in AND past PIN
  if (sessionFlag()) return false;
  if (!isWebPushEnabled() || !hasBrowserPushApis()) return false;
  if (Notification.permission !== "default") return false; // granted: already registered by the manager; denied: never ask
  if (!canShowPushPrompt(userId)) return false;
  if (!(await isPushSupported())) return false;
  try {
    // Respect an explicit "off" in Settings (the default for new users is on).
    const res = await Axios.get(UrlConstant.Settings.usersettings);
    return res?.data?.result === true && res.data.data?.isPushNotification !== false;
  } catch {
    return false;
  }
}

/**
 * Soft ask ("pre-permission") for browser notifications. The browser's own dialog can be answered only once for good,
 * so we first explain the value in our own UI and show the browser dialog ONLY after the user clicks "Turn on".
 *  - never on first paint: after a short delay, on the dashboard, once per session
 *  - "Not now" snoozes 7 days, then 30 days, then stops (3 asks maximum)
 *  - never shown if already allowed, blocked in the browser, switched off in Settings, or unsupported
 */
const EnableNotificationsPrompt: React.FC = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const userId = useRef<string | undefined>(getUserFromLocalStorage()?._id);
  userId.current = getUserFromLocalStorage()?._id;

  useEffect(() => {
    if (!CALM_ROUTES.some((r) => pathname.startsWith(r))) {
      setOpen(false);
      return undefined;
    }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      if (cancelled || !(await isEligible(userId.current)) || cancelled) return;
      sessionFlag(true);
      track("push_prompt_shown");
      setOpen(true);
    }, SHOW_DELAY_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [pathname]);

  const dismiss = () => {
    track("push_prompt_dismissed");
    recordPushPromptDismissed(userId.current);
    setOpen(false);
  };

  const enable = async () => {
    if (busy) return;
    setBusy(true);
    // The browser dialog must start synchronously inside this click (Safari requires a user gesture).
    const permission = await requestPermissionFromUserAction();
    setBusy(false);
    if (permission === "granted") {
      track("push_prompt_accepted");
      clearPushPromptState(userId.current);
      setOpen(false);
      setDone(true);
      await registerDevice();
    } else if (permission === "denied") {
      track("push_prompt_blocked"); // the browser remembers this; we never ask again
      setOpen(false);
    } else if (permission === "default") {
      dismiss(); // closed the browser dialog without choosing
    } else {
      setOpen(false); // unsupported
    }
  };

  return (
    <>
      <Slide in={open} direction="up" mountOnEnter unmountOnExit>
        <Box
          role="region"
          aria-label="Turn on notifications"
          data-testid="enable-notifications-prompt"
          sx={{
            position: "fixed", zIndex: 1200, bottom: 16, right: 16, left: { xs: 16, sm: "auto" }, width: { sm: 380 },
            background: "#141414", color: "#fff", border: "1px solid #ffffff1a", borderRadius: "12px",
            boxShadow: "0 12px 32px rgba(0,0,0,0.5)", borderBottom: "3px solid #f2674a", p: 2,
          }}
        >
          <Box display="flex" alignItems="flex-start" gap={1.5}>
            <NotificationsActiveOutlined sx={{ color: "#f2674a", mt: 0.25 }} />
            <Box flex={1} minWidth={0}>
              <Typography sx={{ fontWeight: 700, fontSize: 15 }}>Never miss an update</Typography>
              <Typography sx={{ fontSize: 13, color: "#d1d5db", mt: 0.5 }}>
                Get alerts for deposits, account activity and announcements from Wolfpack. You can turn this off anytime in Settings.
              </Typography>
              <Box display="flex" gap={1} mt={1.5}>
                <Button
                  data-testid="enable-notifications-accept"
                  disabled={busy}
                  onClick={enable}
                  sx={{ textTransform: "none", fontWeight: 600, fontSize: 13, borderRadius: "50px", px: 2, height: 32, color: "#fff", background: "#f2674a", "&:hover": { background: "#d9573c" }, "&.Mui-disabled": { background: "#f2674a80", color: "#ffffffb3" } }}
                >
                  Turn on notifications
                </Button>
                <Button
                  data-testid="enable-notifications-later"
                  onClick={dismiss}
                  sx={{ textTransform: "none", fontSize: 13, borderRadius: "50px", px: 2, height: 32, color: "#9ca3af", "&:hover": { color: "#fff", background: "#ffffff14" } }}
                >
                  Not now
                </Button>
              </Box>
            </Box>
            <IconButton size="small" aria-label="Close" onClick={dismiss} sx={{ color: "#9ca3af", mt: -0.5, mr: -0.5, "&:hover": { color: "#fff" } }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Slide>
      <Snackbar open={done} autoHideDuration={4000} onClose={() => setDone(false)} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert severity="success" variant="filled" onClose={() => setDone(false)}>
          Notifications are on.
        </Alert>
      </Snackbar>
    </>
  );
};

export default EnableNotificationsPrompt;
